import { useState } from 'react';

const PricingSection = () => {
  const [billingType, setBillingType] = useState<'onetime' | 'monthly'>('onetime');

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small businesses and startups',
      price: billingType === 'onetime' ? 999 : 199,
      period: billingType === 'onetime' ? 'per project' : 'per month',
      icon: 'ri-rocket-line',
      features: [
        'Up to 5 pages website',
        'Mobile responsive design',
        'Basic SEO optimization',
        'Contact form integration',
        '2 rounds of revisions',
        '1 month support',
        'Social media links',
      ],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Professional',
      description: 'Ideal for growing businesses',
      price: billingType === 'onetime' ? 2499 : 499,
      period: billingType === 'onetime' ? 'per project' : 'per month',
      icon: 'ri-vip-crown-line',
      features: [
        'Up to 15 pages website',
        'Custom UI/UX design',
        'Advanced SEO package',
        'CMS integration',
        'Blog setup',
        'E-commerce (up to 50 products)',
        '5 rounds of revisions',
        '3 months support',
        'Performance optimization',
        'Analytics setup',
      ],
      cta: 'Most Popular',
      popular: true,
    },
    {
      name: 'Enterprise',
      description: 'For large-scale projects',
      price: billingType === 'onetime' ? 4999 : 999,
      period: billingType === 'onetime' ? 'per project' : 'per month',
      icon: 'ri-building-4-line',
      features: [
        'Unlimited pages',
        'Custom web application',
        'Full-stack development',
        'Advanced security features',
        'API integrations',
        'Unlimited e-commerce products',
        'Unlimited revisions',
        '12 months priority support',
        'Dedicated project manager',
        'Training & documentation',
        'Source code ownership',
      ],
      cta: 'Contact Us',
      popular: false,
    },
  ];

  return (
    <section id="pricing" className="relative w-full bg-background-50 border-t border-background-200 py-14 md:py-16">
      <div className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                <i className="ri-price-tag-3-line text-xs"></i>
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">
                07 / Pricing
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight text-foreground-900">
              Transparent{' '}
              <span className="block text-primary-500">Pricing.</span>
            </h2>
            <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-[15px] leading-relaxed">
              Choose the perfect plan for your business needs. All plans include our award-winning support and quality guarantee.
            </p>
          </div>

          <div className="flex justify-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-1 bg-background-100 rounded-full p-1 border border-background-200">
              <button
                onClick={() => setBillingType('onetime')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  billingType === 'onetime'
                    ? 'bg-background-50 text-foreground-900 border border-background-200'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                One-time Payment
              </button>
              <button
                onClick={() => setBillingType('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 cursor-pointer whitespace-nowrap ${
                  billingType === 'monthly'
                    ? 'bg-background-50 text-foreground-900 border border-background-200'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Monthly
                {billingType !== 'monthly' && (
                  <span className="bg-primary-500 text-background-50 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full">-20%</span>
                )}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-background-100 rounded-2xl transition-all duration-500 hover:-translate-y-1 ${
                  plan.popular
                    ? 'border-2 border-foreground-900'
                    : 'border border-background-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="bg-primary-500 text-background-50 px-4 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="p-7 md:p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-foreground-900 flex items-center justify-center">
                      <i className={`${plan.icon} text-background-50 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-lg text-foreground-900">{plan.name}</h3>
                      <p className="text-foreground-500 text-xs">{plan.description}</p>
                    </div>
                  </div>

                  <div className="mb-6 pb-6 border-b border-background-200">
                    <div className="flex items-baseline gap-1">
                      <span className="text-foreground-500 text-lg">$</span>
                      <span className="font-display text-5xl font-bold text-foreground-900 tabular-nums">{plan.price}</span>
                    </div>
                    <p className="text-foreground-500 text-xs font-mono uppercase tracking-widest mt-1">{plan.period}</p>
                  </div>

                  <a
                    href="#contact"
                    className={`block w-full py-3 px-6 rounded-full text-center text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap mb-6 ${
                      plan.popular
                        ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                        : 'bg-background-50 text-foreground-900 border border-background-300 hover:border-foreground-900'
                    }`}
                  >
                    {plan.cta}
                  </a>

                  <div className="space-y-3">
                    {plan.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.popular ? 'bg-primary-500/10' : 'bg-background-200'}`}>
                          <i className={`ri-check-line text-xs ${plan.popular ? 'text-primary-500' : 'text-foreground-600'}`}></i>
                        </div>
                        <span className="text-foreground-600 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-foreground-500 text-sm">
              Need a custom solution?{' '}
              <a href="#contact" className="text-primary-500 font-medium hover:underline">
                Contact us
              </a>{' '}
              for a tailored quote.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;